import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import { validateEmail } from '../utils/validation';
import { trackEvent } from '../utils/analytics';
import { ErrorMessage } from './common';
import LanguageSwitcher from './LanguageSwitcher';
import './Auth.css';

function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendStatus, setResendStatus] = useState('idle');
  const [resendMessage, setResendMessage] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    trackEvent('login_submit');
    setError('');
    setEmailNotVerified(false);
    setResendStatus('idle');
    setResendMessage('');

    const emailError = validateEmail(email, t);
    if (emailError) {
      trackEvent('login_failed', { reason: 'invalid_email_format' });
      setError(emailError);
      return;
    }

    setLoading(true);
    const result = await login(email, password);

    if (result.success) {
      trackEvent('login_success');
      navigate('/');
    } else {
      if (result.error === 'EMAIL_NOT_VERIFIED') {
        trackEvent('login_failed', { reason: 'email_not_verified' });
        setEmailNotVerified(true);
        setError(t('auth.login.emailNotVerified'));
      } else if (result.error === 'INVALID_CREDENTIALS') {
        trackEvent('login_failed', { reason: 'invalid_credentials' });
        setError(t('auth.login.invalidCredentials'));
      } else {
        trackEvent('login_failed', { reason: 'unknown_error' });
        setError(result.error);
      }
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    trackEvent('verify_email_resend_submit');
    setResendStatus('loading');
    setResendMessage('');

    try {
      const response = await authService.resendVerification(email);
      trackEvent('verify_email_resend_success');
      setResendStatus('success');
      setResendMessage(response.message);
    } catch (err) {
      trackEvent('verify_email_resend_failed');
      setResendStatus('error');
      setResendMessage(err.response?.data?.message || t('auth.login.resendFailed'));
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>{t('auth.login.title')}</h2>
          <LanguageSwitcher />
        </div>

        <ErrorMessage message={error} />

        {emailNotVerified && (
          <div className="verification-section">
            {resendStatus === 'success' ? (
              <div className="success-alert">{resendMessage}</div>
            ) : (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={handleResendVerification}
                  disabled={resendStatus === 'loading'}
                >
                  {resendStatus === 'loading' ? t('auth.login.sending') : t('auth.login.resendVerification')}
                </button>
                {resendStatus === 'error' && (
                  <p className="error-text">{resendMessage}</p>
                )}
              </>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">{t('auth.login.email')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder={t('auth.login.emailPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.login.password')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              placeholder={t('auth.login.passwordPlaceholder')}
              minLength="6"
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? t('auth.login.submitting') : t('auth.login.submit')}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/" className="link-button">
            {t('auth.login.toHome')}
          </Link>
          <Link to="/forgot-password" className="link-button">
            {t('auth.login.forgotPassword')}
          </Link>
          <Link to="/register" className="link-button">
            {t('auth.login.noAccount')}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
